import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import AssetLiabilityManager from './AssetLiabilityManager';
import NetWorthChart from './NetWorthChart';
import Budget from './Budget';
import Reminders from './Reminders';
import Goals from './Goals';
import Debt from './Debt';
import MealPrep from './MealPrep';
import { TrendingUp, Wallet, Target, TrendingDown, UtensilsCrossed, LogOut } from 'lucide-react';

const Dashboard = ({ session }) => {
  const [assets, setAssets] = useState([]);
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('networth'); // 'networth' | 'budget'

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [assetsResponse, liabilitiesResponse] = await Promise.all([
        supabase.from('assets').select('*').eq('user_id', session.user.id).order('date', { ascending: false }),
        supabase.from('liabilities').select('*').eq('user_id', session.user.id).order('date', { ascending: false })
      ]);

      if (assetsResponse.error) throw assetsResponse.error;
      if (liabilitiesResponse.error) throw liabilitiesResponse.error;

      setAssets(assetsResponse.data || []);
      setLiabilities(liabilitiesResponse.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateNetWorth = () => {
    const totalAssets = assets.reduce((sum, asset) => sum + (parseFloat(asset.value) || 0), 0);
    const totalLiabilities = liabilities.reduce((sum, liability) => sum + (parseFloat(liability.value) || 0), 0);
    return totalAssets - totalLiabilities;
  };

  const getTotalAssets = () => {
    return assets.reduce((sum, asset) => sum + (parseFloat(asset.value) || 0), 0);
  };

  const getTotalLiabilities = () => {
    return liabilities.reduce((sum, liability) => sum + (parseFloat(liability.value) || 0), 0);
  };

  // formatCurrency is now handled in NetWorthChart component

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl text-gray-600">Loading your data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Title row */}
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Lahn Lab" className="h-10 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 leading-tight">Lahn Lab</h1>
                <p className="text-sm text-gray-500">Personal Operating System</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="btn-outline btn-sm"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Sign Out
            </button>
          </div>

          {/* Tab navigation */}
          <nav className="flex gap-x-6 overflow-x-auto">
            {[
              { key: 'networth', label: 'Net Worth', Icon: TrendingUp },
              { key: 'budget', label: 'Budget', Icon: Wallet },
              { key: 'debt', label: 'Debt Payoff', Icon: TrendingDown },
              { key: 'goals', label: 'Goals', Icon: Target },
              { key: 'mealprep', label: 'Meal Prep', Icon: UtensilsCrossed },
            ].map(({ key, label, Icon }) => (
              <button
                key={key}
                onClick={() => setView(key)}
                className={`flex items-center gap-1.5 py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap shrink-0 ${
                  view === key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {view === 'networth' ? (
          <>
            {/* Stay-on-track reminder */}
            <Reminders session={session} />

            {/* Net Worth Summary and Chart Section */}
            <div className="mb-8">
              <NetWorthChart
                session={session}
                currentNetWorth={calculateNetWorth()}
                totalAssets={getTotalAssets()}
                totalLiabilities={getTotalLiabilities()}
                netWorthSummary={{
                  netWorth: calculateNetWorth(),
                  totalAssets: getTotalAssets(),
                  totalLiabilities: getTotalLiabilities(),
                  asOfDate: new Date().toLocaleDateString()
                }}
              />
            </div>

            {/* Assets and Liabilities */}
            <AssetLiabilityManager
              session={session}
              assets={assets}
              liabilities={liabilities}
              onAssetsChange={() => fetchData()}
              onLiabilitiesChange={() => fetchData()}
            />
          </>
        ) : view === 'budget' ? (
          <Budget session={session} liabilities={liabilities} />
        ) : view === 'debt' ? (
          <Debt session={session} liabilities={liabilities} />
        ) : view === 'goals' ? (
          <Goals session={session} assets={assets} />
        ) : (
          <MealPrep session={session} />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/logo.png"
                alt="Lahn Lab"
                className="h-8 w-auto"
              />
              <span className="text-gray-600 text-sm">Your personal operating system</span>
            </div>
            <div className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Lahn Lab. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;